import { useState, useEffect, useCallback, memo, lazy, Suspense } from 'react';
import { useAnimationFrame, useMotionValue, useSpring, motion } from 'framer-motion';
import "@/App.css";
import Lenis from 'lenis';
import LogoIntro from '@/components/LogoIntro';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import ScrollProgress from '@/components/ScrollProgress';

// Lazy load below-fold components to reduce initial bundle
const About = lazy(() => import('@/components/About'));
const Services = lazy(() => import('@/components/Services'));
const Projects = lazy(() => import('@/components/Projects'));
const Pricing = lazy(() => import('@/components/Pricing'));
const Process = lazy(() => import('@/components/Process'));
const Team = lazy(() => import('@/components/Team'));
const Videos = lazy(() => import('@/components/Videos'));
const Testimonials = lazy(() => import('@/components/Testimonials'));
const Contact = lazy(() => import('@/components/Contact'));
const Footer = lazy(() => import('@/components/Footer'));

// React.memo prevents re-render cascade when theme changes
const MemoHero = memo(Hero);
const MemoScrollProgress = memo(ScrollProgress);

function App() {
  const [introComplete, setIntroComplete] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('gruha-theme') || 'dark');

  // Lenis smooth scroll — synced to Framer Motion's animation frame
  const [lenis, setLenis] = useState(null);

  useEffect(() => {
    if (!introComplete) return;
    const l = new Lenis({
      lerp: 0.08,
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.8,
      touchMultiplier: 1.5,
      autoRaf: false,
      syncTouch: true,
    });
    setLenis(l);
    return () => {
      l.destroy();
      setLenis(null);
    };
  }, [introComplete]);

  // Single shared rAF loop — Lenis piggybacks on Framer Motion's loop
  useAnimationFrame((time) => {
    lenis?.raf(time);
  });

  // Theme toggle
  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
    localStorage.setItem('gruha-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => setTheme(prev => prev === 'dark' ? 'light' : 'dark'), []);

  // Cursor glow — driven entirely by Framer Motion values (no DOM reads on mousemove)
  const mouseX = useMotionValue(-500);
  const mouseY = useMotionValue(-500);

  const springConfig = { damping: 25, stiffness: 200, mass: 0.5 };
  const cursorX = useSpring(mouseX, springConfig);
  const cursorY = useSpring(mouseY, springConfig);

  useEffect(() => {
    const move = (e) => {
      mouseX.set(e.clientX - 125);
      mouseY.set(e.clientY - 125);
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, [mouseX, mouseY]);

  const handleIntroComplete = useCallback(() => setIntroComplete(true), []);

  return (
    <div className="app-root" data-testid="app-root" data-theme={theme}>
      {!introComplete && <LogoIntro onComplete={handleIntroComplete} />}

      {theme === 'dark' && (
        <motion.div
          className="cursor-glow hidden md:block"
          style={{ x: cursorX, y: cursorY }}
        />
      )}

      {introComplete && <MemoScrollProgress />}
      {introComplete && <Navbar theme={theme} toggleTheme={toggleTheme} />}

      {introComplete && (
        <main>
          <MemoHero />
          <Suspense fallback={null}>
            <About />
            <Services />
            <Projects />
            <Pricing />
            <Process />
            <Team />
            <Videos />
            <Testimonials />
            <Contact />
            <Footer />
          </Suspense>
        </main>
      )}
    </div>
  );
}

export default App;
